import React, { useState, useEffect } from 'react';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import { setPageTitle } from '../../utils/pageTitle';
import { 
  BookOpen, 
  LayoutDashboard, 
  Settings, 
  Shield, 
  Home,
  ChevronLeft,
  Menu,
  X
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { employeeNamesService } from '../../services/employeeNames';
import { UserInfoSection } from '../layout/UserInfoSection';
import { useDarkMode } from '../../context/DarkModeContext';

/**
 * Layout component for the AI DevQuest app
 */
const AIDevQuestLayout: React.FC = () => {
  const [displayName, setDisplayName] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();
  const { userEmail } = useAuth();
  const { isDarkMode } = useDarkMode();
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 768);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    const loadDisplayName = async () => {
      if (userEmail) {
        const name = await employeeNamesService.getNameFromEmail(userEmail);
        if (name) {
          setDisplayName(name);
        }
      }
    };
    loadDisplayName();
  }, [userEmail]);

  // Set page title based on current route
  useEffect(() => {
    const path = location.pathname;
    let title = 'AI DevQuest';
    
    if (path === '/ai-devquest') {
      title = 'AI DevQuest';
    } else if (path.startsWith('/ai-devquest/settings')) {
      title = 'Settings | AI DevQuest';
    }
    
    setPageTitle(title);
  }, [location.pathname]);
  
  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };
  
  const { isAdmin } = useIsAdmin();
  
  const handleNavigate = (to: string) => {
    navigate(to);
    setIsMobileMenuOpen(false);
  };
  
  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex overflow-hidden">
      {/* Mobile Menu Button */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-800 z-40 border-b dark:border-gray-700 px-4 flex items-center">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6 text-gray-600" />
          ) : (
            <Menu className="h-6 w-6 text-gray-600" />
          )}
        </button>
      </div>

      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          bg-white dark:bg-gray-800 shadow-sm fixed md:relative h-full z-40
          transform transition-transform duration-300 ease-in-out flex flex-col
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0 hidden md:flex'}
          ${isCollapsed && !isMobileMenuOpen ? 'w-16 md:w-16' : 'w-64 md:w-64'}
        `}
      >
        {/* Collapse Toggle - Hidden on Mobile */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-6 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-full p-1 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 hidden md:block"
        >
          <ChevronLeft className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
        </button>

        <div 
          className="border-b flex justify-center items-center h-46 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
          onClick={() => handleNavigate('/ai-devquest')}
          title="Go to Dashboard"
        >
          {isCollapsed && !isMobileMenuOpen ? (
            <BookOpen className="h-12 w-12 text-gray-500 mb-1 mt-1 transition-all duration-300" />
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <BookOpen className="h-20 w-20 text-gray-500 mb-2" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">AI DevQuest</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Coding with AI Ecourse</p>
            </div>
          )}
        </div>
        
        <nav className="flex-1 mt-4 overflow-y-auto flex flex-col">
          {/* Main Navigation */}
          <div
            onClick={() => handleNavigate('/ai-devquest')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/ai-devquest') && !isActive('/ai-devquest/settings') && !isActive('/ai-devquest/admin') 
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' 
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <LayoutDashboard className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Dashboard'}
          </div>

          {/* Divider */}
          <div className="mx-4 my-4 border-t border-gray-200 dark:border-gray-700" />

          {/* Utility Navigation */}
          <div
            onClick={() => handleNavigate('/ai-devquest/settings')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/ai-devquest/settings') 
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' 
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Settings className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Settings'}
          </div>

          {/* Admin Link - Only shown to admin users */}
          {isAdmin && (
            <div
              onClick={() => handleNavigate('/ai-devquest/admin')}
              className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                isActive('/ai-devquest/admin') 
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' 
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <Shield className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
              {(!isCollapsed || isMobileMenuOpen) && 'Admin'}
            </div>
          )}

          {/* Apps Hub Link */}
          <div
            onClick={() => handleNavigate('/dashboard')}
            className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
          >
            <Home className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Apps Hub'}
          </div>
        </nav>

        {/* User Info Section */}
        <UserInfoSection displayName={displayName} isCollapsed={isCollapsed && !isMobileMenuOpen} />
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-auto pt-16 md:pt-0 relative">
        <Outlet />
      </main>
    </div>
  );
};

export default AIDevQuestLayout;